import {
  CondensedButton,
  Flex,
  Heading,
  IconDownloadLine,
  Text,
  View,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Card } from "../../../components/Card";
import {
  ERROR,
  INFO,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { rteHasContent } from "../../../components/RichTextArea/RichTextEditor";
import RichTextViewer from "../../../components/RichTextArea/RichTextViewer";
import { Files } from "../../../services/FilesService";
import downloadFile from "../../../utils/Dom";
import PublicStructuredQuestionResponse from "./PublicStructuredQuestionResponse";

const PublicQualitativeMeasure = ({
  measure,
  tenantId,
  structuredQuestions = [],
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [evidence, setEvidence] = useState(null);
  const [evidenceError, setEvidenceError] = useState(false);
  const [instructions, setInstructions] = useState(null);
  const [files, setFiles] = useState([]);

  const hasStructuredQuestions =
    Array.isArray(structuredQuestions) && structuredQuestions.length > 0;

  useEffect(() => {
    if (!measure.upload_evidence) return;

    const fetchFiles = async () => {
      try {
        let paths = await Files.getPublicFiles(
          "public-qualitativeEvidence",
          measure.ddl_id,
          tenantId
        );
        paths = paths.filter((file) => file.file_name !== "response.html");
        setFiles(paths);
      } catch {
        showNotification(
          t("notifications.assessmentResults.downloadMeasureEvidenceError"),
          ERROR
        );
      }
    };

    fetchFiles();
  }, [measure, tenantId]);

  useEffect(() => {
    const getInstructions = async () => {
      try {
        const measureInstructions = await Files.getPublicFileContent(
          "response.html",
          "public-qualitativeMeasure",
          measure.ddl_id,
          tenantId
        );
        setInstructions(measureInstructions.content);
      } catch {
        setInstructions("");
        showNotification(
          t("notifications.assessmentResults.getMeasureError"),
          ERROR
        );
      }
    };

    getInstructions();
  }, [measure.ddl_id, tenantId, showNotification]);

  useEffect(() => {
    if (hasStructuredQuestions) {
      setEvidence("");
      setEvidenceError(false);
      return;
    }

    const getFileContent = async () => {
      try {
        const evidence = await Files.getPublicFileContent(
          "response.html",
          "public-qualitativeEvidence",
          measure.ddl_id,
          tenantId
        );
        setEvidence(evidence.content || "");
        setEvidenceError(false);
      } catch {
        setEvidence("");
        setEvidenceError(true);
        showNotification(
          t("notifications.assessmentResults.getMeasureRetrievalError", {
            measureName: measure.name,
          }),
          ERROR
        );
      }
    };

    getFileContent();
  }, [measure.ddl_id, tenantId, showNotification, hasStructuredQuestions]);

  function downloadMeasureEvidence() {
    try {
      if (files.length > 0) {
        files.forEach((element) => {
          downloadFile(element.file_url, element.file_name);
        });
      } else {
        showNotification(
          t("results.noEvidenceForMeasure", { measureName: measure.name }),
          INFO
        );
      }
    } catch {
      showNotification(
        t("notifications.assessmentResults.downloadMeasureEvidenceError"),
        ERROR
      );
    }
  }

  const isLoading = evidence === null || instructions === null;

  if (isLoading) {
    return (
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="100%">
          <Heading level="h3">{measure.name}</Heading>
        </Flex.Item>
        <Flex.Item shouldGrow>
          <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />
        </Flex.Item>
      </Flex>
    );
  }

  // Structured questions mode
  if (hasStructuredQuestions) {
    return (
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="100%">
          <Heading level="h3">{measure.name}</Heading>
        </Flex.Item>
        {rteHasContent(instructions) && (
          <Flex.Item size="100%" padding="0 0 0 large">
            <View
              as="div"
              borderWidth="none none small none"
              padding="0 0 x-small 0"
            >
              <RichTextViewer
                id={`public-qual-instructions-${measure.ddl_id}`}
                value={instructions}
                ariaLabel={t("assessment.instructions")}
              />
            </View>
          </Flex.Item>
        )}
        <Flex.Item size="100%" padding="0 0 0 large">
          <PublicStructuredQuestionResponse
            questions={structuredQuestions}
            measureId={measure.ddl_id}
            tenantId={tenantId}
          />
        </Flex.Item>
      </Flex>
    );
  }

  // Unstructured mode
  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{measure.name}</Heading>
      </Flex.Item>
      {rteHasContent(instructions) && (
        <Flex.Item size="100%" padding="0 0 0 large">
          <View
            as="div"
            borderWidth="none none small none"
            padding="0 0 x-small 0"
          >
            <RichTextViewer
              value={instructions}
              ariaLabel={t("assessment.instructions")}
            />
          </View>
        </Flex.Item>
      )}
      <Flex.Item size="100%" padding="0 0 0 large">
        <Card style={{ border: "1px solid #ccc" }}>
          {evidenceError ? (
            <Text color="danger">
              {t("notifications.assessmentResults.getMeasureRetrievalError", {
                measureName: measure.name,
              })}
            </Text>
          ) : rteHasContent(evidence) ? (
            <RichTextViewer
              id={measure.ddl_id}
              ariaLabel={t("results.measureResults")}
              value={evidence}
            />
          ) : (
            <Text fontStyle="italic">{t("results.noResponsePublished")}</Text>
          )}
          {files.length > 0 && (
            <View
              as="div"
              borderWidth="small 0 0 0"
              textAlign="end"
              margin="small 0 0 0"
              padding="small 0 0 0"
            >
              <CondensedButton
                renderIcon={IconDownloadLine}
                onClick={downloadMeasureEvidence}
              >
                {`${t("results.downloadMeasureEvidence")} (${files.length})`}
              </CondensedButton>
            </View>
          )}
        </Card>
      </Flex.Item>
    </Flex>
  );
};

export default PublicQualitativeMeasure;
